// Lib
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { get, isEmpty, isEqual } from 'lodash';
import { ContentState, convertFromRaw, convertToRaw } from 'draft-js';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';

// Components
import TableCellEditor from '../../../components/editor/tableCell/TableCellEditor';

// Utils
import { parseCellContentString } from '../utils/tableInitialisationUtils';
import {
    getTableContentCellTextStyle,
    getTableContentCellVerticalAlignment,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getFirstSelectionStartCellCoords } from '../utils/tableCellSelectionUtils';
import {
    getBackgroundColorClass,
    getCellTextAlignment,
    getCellLevelTextStyles,
    getLocalisedContent,
    getCellEditingAlignment,
} from '../utils/tableCellFormattingUtils';
import { getCellVerticalAlignmentClassName } from '../utils/tableDOMUtils';
import { getCurrentlyEditingEditorId } from '../../selection/currentlyEditingSelector';
import applyInlineStylesThroughoutContent from '../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';
import { isFormula } from '../utils/tableFormulaUtils';
import { getTableCellEditorId } from '../utils/tableCellEditingUtils';

// Constants
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { CellTypeNames, TextAlignment } from '../cellTypes/CellTypeConstants';

const mapStateToProps = createStructuredSelector({
    currentlyEditingEditorId: getCurrentlyEditingEditorId,
});

const MilanoteCellEditorComponent = (props) => {
    const {
        element,
        elementId,
        isEditable,
        tableContentData,
        currentlyEditingEditorId,
        isCurrentlyEditingTableCell,
        hotTableContainerRef,
        hotTableInstanceRef,
        cellEditorRef,
        currentCellSelections,
    } = props;

    const activeCellCoords = getFirstSelectionStartCellCoords(currentCellSelections);
    const { row, col } = activeCellCoords;
    const verticalAlignment = getTableContentCellVerticalAlignment(activeCellCoords)(element);
    const initialTextStyle = getTableContentCellTextStyle(activeCellCoords)(element);

    const [textAlignment, setTextAlignment] = useState(null);
    const [textStyle, setTextStyle] = useState(null);

    // Set initial text alignment and styles
    useEffect(() => {
        if (!currentCellSelections) return;

        setTextStyle(Array.from(initialTextStyle || []));

        const cellData = get(tableContentData, [row, col]);

        // For the editor only, we want to ensure that date types are right aligned
        // Even if the initial value is text, the datePicker will be opened with the current date
        const isDateTimeString = !isFormula(cellData?.value) && cellData?.type?.name === CellTypeNames.DATE_TIME;
        const editorTextAlignment = isDateTimeString ? TextAlignment.RIGHT : getCellTextAlignment(cellData);

        setTextAlignment(editorTextAlignment);
    }, [row, col, isCurrentlyEditingTableCell]);

    const textContentInCurrentSelection = useMemo(() => {
        // IMPORTANT: Only update the textContent when user starts editing. This is to prevent a bug where text from
        // previously edited cell is still present in the cell editor when editing a different empty cell.
        // - MilanoteEditor has some optimisations that only updates its internal editorState state when isCurrentlyEditingTableCell
        //   is toggled.
        if (!isCurrentlyEditingTableCell) return;
        if (!currentCellSelections) return undefined;

        const cellData = get(tableContentData, [activeCellCoords.row, activeCellCoords.col]);
        const { value: cellValue, type } = cellData || {};

        if (cellValue === null && initialTextStyle === null) return null;

        const content = parseCellContentString(cellValue || '');
        const localisedContent = getLocalisedContent(content, type?.name);

        if (isEmpty(initialTextStyle) || isFormula(cellValue || '')) return localisedContent;

        let contentState =
            typeof localisedContent === 'object'
                ? convertFromRaw(localisedContent)
                : ContentState.createFromText(localisedContent.toString());

        contentState = applyInlineStylesThroughoutContent(contentState, initialTextStyle);

        return convertToRaw(contentState);
    }, [isCurrentlyEditingTableCell, initialTextStyle]);

    const backgroundClasses = useMemo(() => {
        if (!isCurrentlyEditingTableCell || !currentCellSelections) return;
        const cellData = get(tableContentData, [activeCellCoords.row, activeCellCoords.col]);
        return getBackgroundColorClass(cellData);
    }, [isCurrentlyEditingTableCell]);

    const onChange = useCallback(
        ({ editorState }) => {
            if (!hotTableInstanceRef.current) return;

            const cellData = get(tableContentData, [activeCellCoords.row, activeCellCoords.col]);
            const currentContent = editorState.getCurrentContent();
            const editCellValue = currentContent.getPlainText();

            // check whether alignment should be updated
            const updateAlignment = getCellEditingAlignment(editCellValue, cellData);
            if (updateAlignment) setTextAlignment(updateAlignment);

            // update the text style if the user has changed the inline styles
            if (editCellValue.length > 0) {
                const cellLevelTextStyles = currentContent ? getCellLevelTextStyles(currentContent) : [];
                if (!isEqual(cellLevelTextStyles.sort(), textStyle?.sort() || [])) {
                    setTextStyle(cellLevelTextStyles);
                }
            }

            hotTableInstanceRef.current.runHooks('onMilanoteCellEditorChange', editorState);
        },
        [row, col, textStyle],
    );

    if (!hotTableContainerRef.current) return null;

    return createPortal(
        <div className={classNames('MilanoteCellEditorComponent', backgroundClasses)}>
            <div className="MilanoteCellEditorComponent-relative">
                <div className="handsontable-input-positioner">
                    <div
                        className={classNames(
                            'handsontable-input-holder',
                            getCellVerticalAlignmentClassName(verticalAlignment),
                        )}
                    >
                        <TableCellEditor
                            editorRef={cellEditorRef}
                            className="handsontable-input"
                            element={element}
                            isEditing={isCurrentlyEditingTableCell}
                            isEditable={isEditable}
                            textContent={textContentInCurrentSelection}
                            defaultStyleOverride={textStyle}
                            textAlignment={textAlignment}
                            editorId={getTableCellEditorId(elementId)}
                            editorKey={TABLE_CELL_EDITOR_KEY}
                            currentEditorId={currentlyEditingEditorId}
                            onChange={onChange}
                        />
                    </div>
                </div>
            </div>
        </div>,
        hotTableContainerRef.current,
    );
};

MilanoteCellEditorComponent.propTypes = {
    element: PropTypes.object,
    elementId: PropTypes.string,
    tableContentData: PropTypes.array,
    isEditable: PropTypes.bool,
    currentlyEditingEditorId: PropTypes.string,
    currentlyEditingEditorKey: PropTypes.string,
    textContent: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    isCurrentlyEditingTableCell: PropTypes.bool,

    hotTableContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    cellEditorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

    currentCellSelections: PropTypes.object,
};

export default connect(mapStateToProps)(MilanoteCellEditorComponent);
